<script setup lang="ts">
  import StoreMenuItemPicture from '@/components/shared/StoreMenuItemPicture.vue';
  import { useAuth } from '@/composition/use-auth';
  import { useOrders } from '@/composition/use-orders';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import { IOrderItem } from '@/interfaces/IOrderItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { stringDateToHHMM } from '@/utils/date.utils';
  import { orderItemOptionsToText } from '@/utils/orders.utils';
  import { computed, inject, Ref } from 'vue';

  const restaurantStore = useRestaurantStore();
  const order = inject<Ref<IOrder>>('order')!;
  const orderHelper = useOrders();
  const auth = useAuth();

  // Editing requires OrdersManage (the edit flow routes into the POS): an
  // OrdersHistoric-only back-office user must not see the entry point.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  function getItemOptions(orderItem: IOrderItem) {
    let summary = orderItemOptionsToText({
      orderItem,
    });

    return summary;
  }

  function editOrder() {
    orderHelper.editOrder(order.value);
  }

  function getItemNote(
    orderItem: IOrderItem
  ): { text: string; icon: string; color: string } | undefined {
    if (orderItem.canceledAt) {
      return {
        text: `Eliminado a las ${stringDateToHHMM(orderItem.canceledAt)}`,
        icon: 'mdi-delete',
        color: 'error',
      };
    } else if (orderItem.addedAt) {
      return {
        text: `Añadido a las ${stringDateToHHMM(orderItem.addedAt)}`,
        icon: 'mdi-plus',
        color: 'success',
      };
    } else if (orderItem.modifiedAt) {
      return {
        text: `Modificado a las ${stringDateToHHMM(orderItem.modifiedAt)}`,
        icon: 'mdi-pencil',
        color: 'warning',
      };
    }

    return undefined;
  }

  const itemNotes = computed(() => {
    const notes: (
      | { text: string; icon: string; color: string }
      | undefined
    )[] = [];

    for (const orderItem of order.value.orderItems ?? []) {
      const note = getItemNote(orderItem);
      notes.push(note);
    }

    return notes;
  });
</script>

<template>
  <div class="view-order-items">
    <div class="items-header">
      <v-icon>mdi-cart</v-icon>
      <h3>Productos</h3>
      <div class="spacer"></div>
      <v-btn
        v-if="canManageOrders"
        small
        color="info"
        dark
        depressed
        rounded
        @click="editOrder"
      >
        <v-icon left small>mdi-pencil</v-icon>
        Agregar / Quitar
      </v-btn>
    </div>
    <div class="items-content">
      <template v-if="!order.orderItems?.length">
        <div class="empty-state">
          <p>No hay productos en el pedido</p>
        </div>
      </template>
      <template v-else>
        <div
          class="product"
          v-for="(orderItem, index) in order.orderItems"
          :key="orderItem.itemId"
          :class="{
            cancelled: !!orderItem.canceledAt,
          }"
        >
          <div class="quantity">{{ orderItem.amount }}x</div>
          <div class="picture-container">
            <StoreMenuItemPicture class="picture" :item="orderItem.item" />
          </div>
          <div class="details">
            <span class="name">{{ orderItem.item.name }}</span>
            <span class="options" v-html="getItemOptions(orderItem)"></span>
            <span v-if="orderItem.comment" class="comment">
              <v-icon x-small>mdi-comment</v-icon>
              {{ orderItem.comment }}</span
            >
            <span class="price">
              {{ restaurantStore.currency?.symbol }}{{ orderItem.total }}
            </span>
            <span v-if="itemNotes[index]" class="note">
              <v-icon x-small :color="itemNotes[index].color">{{
                itemNotes[index].icon
              }}</v-icon>
              {{ itemNotes[index].text }}
            </span>
          </div>
          <template v-if="orderItem.printC === orderItem.amount">
            <v-chip
              v-tooltip="'Ya impreso en comanda'"
              class="printed-indicator"
              x-small
              color="info"
            >
              <v-icon x-small>mdi-check</v-icon>
              <v-icon x-small>mdi-printer</v-icon>
            </v-chip>
          </template>
          <template v-else>
            <v-chip
              v-tooltip="'No impreso en comanda'"
              class="printed-indicator"
              x-small
              color="grey"
              dark
            >
              <v-icon x-small>mdi-printer-off</v-icon>
            </v-chip>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .view-order-items {
    .items-header {
      @apply tw-px-2 tw-py-1 tw-flex tw-items-center tw-space-x-2 tw-text-gray-700;
    }

    .items-content {
      @apply tw-flex tw-flex-col tw-w-full tw-divide-y tw-divide-dashed tw-divide-gray-200 tw-pb-3 tw-px-2;

      & > * {
        @apply tw-py-3;
      }
    }

    .footer {
    }
  }

  .empty-state {
    @apply tw-flex tw-items-center tw-justify-center tw-my-6;
  }

  .product {
    @apply tw-flex tw-items-center tw-space-x-3 tw-py-3 tw-text-sm tw-relative;

    .printed-indicator {
      @apply tw-absolute tw-top-1 tw-right-0 tw-px-1;
    }

    .quantity {
      @apply tw-font-semibold tw-flex-none;
    }

    .picture-container {
      @apply tw-flex-none;
    }

    .details {
      @apply tw-flex tw-flex-col;

      .name {
        @apply tw-font-semibold;
      }

      .original-name {
        @apply tw-text-gray-600 tw-text-xs;
      }

      .options {
        @apply tw-text-gray-700 tw-text-xs;
      }

      .comment {
        @apply tw-text-gray-700 tw-text-xs;
      }

      .price {
        @apply tw-font-medium;
      }

      .note {
        @apply tw-space-x-1 tw-text-gray-500 tw-text-xs;
        text-decoration: none;
      }
    }

    &.cancelled {
      @apply tw-line-through tw-opacity-50;
    }
  }
</style>
